import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProfileSlideInModalComponent } from '@app/shared/ui/profile-slide-in-modal/profile-slide-in-modal.component';
import { PushNotificationService } from '@app/core/services/client/push-notification.service';
import { NotificationStoreService } from '@app/core/services/client/notification-store.service';
import { NotificationFacade } from '@app/core/facades/notification.facade';

@Component({
  selector: 'app-notifications-modal',
  standalone: true,
  imports: [CommonModule, ProfileSlideInModalComponent],
  templateUrl: './notifications-modal.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsModalComponent implements OnInit {
  @Output() action = new EventEmitter<void>();

  private pushService = inject(PushNotificationService);
  private store = inject(NotificationStoreService);
  private notify = inject(NotificationFacade);
  private change = inject(ChangeDetectorRef);

  // 'default' = not yet asked, 'granted' = enabled, 'denied' = blocked.
  permission: NotificationPermission | 'unsupported' = 'default';
  isEnabling = false;

  notifications = this.store.items;

  ngOnInit(): void {
    this.permission = this.readPermission();
    // Pull the latest feed, then clear the unread badge for what's now shown.
    this.store.refresh();
    this.store.markAllRead();
  }


  openAction(url: string | null): void {
    if (!url) return;
    const target = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    window.open(target, '_blank', 'noopener');
  }

  timeAgo(iso: string | null): string {
    if (!iso) return '';
    const seconds = Math.floor((Date.now() - Date.parse(iso)) / 1000);
    if (seconds < 0 || isNaN(seconds)) return '';
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  async enableNotifications(): Promise<void> {
    this.isEnabling = true;
    this.change.markForCheck();
    try {
      await this.pushService.requestPermissionAndRegister();
      this.permission = this.readPermission();
      if (this.permission === 'granted') {
        this.notify.success('Notifications enabled successfully');
        window.location.reload();
        return;
      } else if (this.permission === 'denied') {
        this.notify.error(
          'Notifications are blocked. You can enable them in your browser settings.'
        );
      }
    } catch {
      this.notify.error('Could not enable notifications. Please try again.');
    } finally {
      this.isEnabling = false;
      this.change.markForCheck();
    }
  }

  closeModal(): void {
    this.action.emit();
  }

  private readPermission(): NotificationPermission | 'unsupported' {
    return typeof Notification === 'undefined'
      ? 'unsupported'
      : Notification.permission;
  }
}
